import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Loader2, Search, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../services/api';

/**
 * "Register player(s)" step shown immediately after an admin/staff reserves
 * a new small-group slot from the lane calendar. Lets them debounce-search
 * for any player or parent (via /api/admin/global-search) and register one
 * or more players against the freshly-created `slot_id`.
 *
 * - Search query is debounced 250ms.
 * - Results show players directly, AND parents/customers (expanding a parent
 *   reveals their children).
 * - Selected players appear as removable pills above the search box.
 * - Submit registers each selected player in parallel via
 *   POST /api/small-group/registrations, reporting per-row success/failure.
 */
const useDebouncedValue = (value, delay = 250) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

export default function RegisterPlayersDialog({
  open,
  onOpenChange,
  slot,           // { id, session_name, date, start_time, end_time, ... }
  onDone,
}) {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 250);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);                 // [{type, id, name, ...}]
  const [expandedParent, setExpandedParent] = useState(null); // parent id whose children are loaded
  const [childrenByParent, setChildrenByParent] = useState({}); // parent_id -> [players]

  // selectedPlayers: array of { id, name, parent_id (optional, for source label) }
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const inputRef = useRef(null);

  // Reset every time the dialog opens for a different slot.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setResults([]);
    setSelectedPlayers([]);
    setExpandedParent(null);
    setChildrenByParent({});
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [open, slot?.id]);

  // Debounced search.
  useEffect(() => {
    if (!open) return;
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setSearching(true);
      try {
        const res = await api.get(`/admin/global-search?q=${encodeURIComponent(debouncedQuery)}`);
        if (cancelled) return;
        const rows = res.data?.results || res.data || [];
        // Only keep players + parents/customers — coaches/staff aren't relevant here.
        const filtered = rows.filter((r) => ['player', 'parent', 'customer'].includes(r.type));
        setResults(filtered);
      } catch (err) {
        if (!cancelled) toast.error('Search failed');
      } finally {
        if (!cancelled) setSearching(false);
      }
    })();
    return () => { cancelled = true; };
  }, [debouncedQuery, open]);

  const toggleParentExpand = async (parentId) => {
    if (expandedParent === parentId) {
      setExpandedParent(null);
      return;
    }
    setExpandedParent(parentId);
    if (!childrenByParent[parentId]) {
      try {
        const res = await api.get(`/parents/${parentId}/children`);
        setChildrenByParent((m) => ({ ...m, [parentId]: res.data || [] }));
      } catch (err) {
        toast.error('Failed to load children');
      }
    }
  };

  const addPlayer = (player) => {
    setSelectedPlayers((sel) => {
      if (sel.some((p) => p.id === player.id)) return sel;
      return [...sel, player];
    });
  };
  const removePlayer = (id) =>
    setSelectedPlayers((sel) => sel.filter((p) => p.id !== id));

  const handleSubmit = async () => {
    if (!slot?.id || selectedPlayers.length === 0) return;
    setSubmitting(true);
    let ok = 0, fail = 0;
    const failures = [];
    await Promise.all(selectedPlayers.map(async (p) => {
      try {
        await api.post('/small-group/registrations', {
          slot_id: slot.id,
          player_id: p.id,
          terms_accepted: true,  // admin/staff bookings — backend stamps consent on behalf
        });
        ok += 1;
      } catch (err) {
        fail += 1;
        failures.push(`${p.name}: ${err.response?.data?.detail || 'failed'}`);
      }
    }));
    setSubmitting(false);

    if (ok > 0) toast.success(`Registered ${ok} player${ok === 1 ? '' : 's'}`);
    if (fail > 0) toast.error(failures.join('; '));

    if (fail === 0) {
      onOpenChange(false);
      if (onDone) onDone({ registered: ok });
    } else {
      // Remove successful ones from the selected list so the user can retry the failures.
      const failedIds = new Set(
        failures.map((m) => selectedPlayers.find((p) => m.startsWith(p.name + ':'))?.id).filter(Boolean),
      );
      setSelectedPlayers((sel) => sel.filter((p) => failedIds.has(p.id)));
    }
  };

  const slotLabel = useMemo(() => {
    if (!slot) return '';
    const parts = [];
    if (slot.session_name) parts.push(slot.session_name);
    if (slot.date) parts.push(slot.date);
    if (slot.start_time) parts.push(slot.start_time + (slot.end_time ? `–${slot.end_time}` : ''));
    return parts.join(' · ');
  }, [slot]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="register-players-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" /> Register Player(s)
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-900">
            Adding to slot: <span className="font-medium">{slotLabel}</span>
          </div>

          {selectedPlayers.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="selected-players-list">
              {selectedPlayers.map((p) => (
                <span
                  key={p.id}
                  className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-800 px-2 py-0.5 text-xs"
                  data-testid={`selected-player-${p.id}`}
                >
                  {p.name}
                  <button
                    type="button"
                    onClick={() => removePlayer(p.id)}
                    className="hover:text-violet-900"
                    aria-label={`Remove ${p.name}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search player or parent by name, email or ID…"
              className="pl-8"
              data-testid="register-search-input"
            />
          </div>

          <div className="border border-slate-200 rounded-md max-h-64 overflow-y-auto bg-white">
            {searching ? (
              <div className="flex items-center justify-center py-6 text-slate-500 text-sm">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Searching…
              </div>
            ) : !query ? (
              <div className="text-center py-6 text-slate-400 text-xs">
                Start typing to find a player or parent.
              </div>
            ) : results.length === 0 ? (
              <div className="text-center py-6 text-slate-400 text-xs">
                No matches.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {results.map((r) => {
                  if (r.type === 'player') {
                    const already = selectedPlayers.some((p) => p.id === r.id);
                    return (
                      <li key={`player-${r.id}`} className="px-3 py-2 flex items-center justify-between hover:bg-slate-50">
                        <div className="min-w-0">
                          <div className="text-sm text-slate-800 truncate">{r.name}</div>
                          <div className="text-[11px] text-slate-500">Player · {r.numeric_id || '—'}</div>
                        </div>
                        <Button
                          size="sm"
                          variant={already ? 'outline' : 'default'}
                          disabled={already}
                          onClick={() => addPlayer({ id: r.id, name: r.name })}
                          data-testid={`add-player-${r.id}`}
                        >
                          {already ? 'Added' : 'Add'}
                        </Button>
                      </li>
                    );
                  }
                  // parent/customer row — expandable
                  const expanded = expandedParent === r.id;
                  const kids = childrenByParent[r.id] || [];
                  return (
                    <li key={`parent-${r.id}`} className="hover:bg-slate-50">
                      <button
                        type="button"
                        onClick={() => toggleParentExpand(r.id)}
                        className="w-full text-left px-3 py-2"
                        data-testid={`expand-parent-${r.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="min-w-0">
                            <div className="text-sm text-slate-800 truncate">{r.name}</div>
                            <div className="text-[11px] text-slate-500">
                              {r.type === 'customer' ? 'Customer' : 'Parent'}
                              {r.email && ` · ${r.email}`}
                            </div>
                          </div>
                          <span className="text-xs text-slate-500">{expanded ? '▾' : '▸'}</span>
                        </div>
                      </button>
                      {expanded && (
                        <ul className="bg-slate-50 px-3 pb-2">
                          {kids.length === 0 ? (
                            <li className="text-[11px] text-slate-500 py-1.5">No children on file.</li>
                          ) : kids.map((c) => {
                            const already = selectedPlayers.some((p) => p.id === c.id);
                            return (
                              <li
                                key={c.id}
                                className="flex items-center justify-between py-1.5 pl-3 border-l-2 border-violet-300"
                              >
                                <div className="text-sm text-slate-700">
                                  {c.full_name || c.name}
                                  <span className="text-[11px] text-slate-400 ml-2">{c.numeric_id || ''}</span>
                                </div>
                                <Button
                                  size="sm"
                                  variant={already ? 'outline' : 'default'}
                                  disabled={already}
                                  onClick={() => addPlayer({ id: c.id, name: c.full_name || c.name })}
                                  data-testid={`add-child-${c.id}`}
                                >
                                  {already ? 'Added' : 'Add'}
                                </Button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="register-skip-btn"
          >
            {selectedPlayers.length === 0 ? 'Skip' : 'Cancel'}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedPlayers.length === 0 || submitting}
            className="bg-violet-600 hover:bg-violet-700"
            data-testid="register-submit-btn"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Registering…</>
            ) : (
              `Register ${selectedPlayers.length || ''} player${selectedPlayers.length === 1 ? '' : 's'}`.trim()
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
